/**
 * TransferirResponsavelCard — usado dentro do painel de edição da ONG/OSC
 * (aba "Governança"). Permite ao responsável atual transferir a titularidade
 * da organização para outro usuário do mesmo município.
 *
 * Após a transferência, o usuário antigo perde acesso ao PATCH e o novo
 * responsável passa a poder editar. Chama POST /org-projects/{orgId}/transferir-responsavel.
 */
import React, { useState } from 'react';
import { UserCog, AlertTriangle, Loader2, ArrowRightLeft, Mail, Copy, ExternalLink, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../services/api';

/**
 * Deriva a URL de cadastro no domínio/tenant atual. Aponta para `/cadastrar`
 * (que exibe a tela de auth com toggle "Entrar / Criar conta"). Passa
 * `modo=cadastro` para forçar a aba "Criar conta" e `email=X` para
 * pré-preencher o campo de email do(a) convidado(a).
 */
function buildRegisterLink(email) {
  const base = typeof window !== 'undefined' ? window.location.origin : '';
  const params = new URLSearchParams();
  params.set('modo', 'cadastro');
  if (email) params.set('email', email);
  return `${base}/cadastrar?${params.toString()}`;
}

export default function TransferirResponsavelCard({
  orgId,
  currentResponsavel,
  onTransferred,
  onConviteCriado,
}) {
  const [email, setEmail] = useState('');
  const [confirmando, setConfirmando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  // Estado especial: 404 do backend → mostra link de cadastro para compartilhar.
  const [emailNaoCadastrado, setEmailNaoCadastrado] = useState(null);

  const handleSubmit = async () => {
    if (!email.trim()) {
      toast.error('Informe o email do novo responsável.');
      return;
    }
    setEnviando(true);
    setEmailNaoCadastrado(null);
    try {
      const res = await api.post(`/org-projects/${orgId}/transferir-responsavel`, {
        novo_responsavel: email.trim().toLowerCase(),
      });
      const novo = res.data?.novo_responsavel;
      toast.success('Responsabilidade transferida com sucesso.');
      onTransferred?.(novo);
      setEmail('');
      setConfirmando(false);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || 'Falha ao transferir responsabilidade.';
      if (status === 404 && /não encontrad/i.test(detail)) {
        const emailAlvo = email.trim().toLowerCase();
        setEmailNaoCadastrado(emailAlvo);
        setConfirmando(false);
        // Registra convite pendente para aparecer no card ao lado —
        // permite reexecutar depois sem digitar o email de novo.
        try {
          await api.post(`/org-projects/${orgId}/convites-transferencia`, {
            email_convidado: emailAlvo,
          });
          onConviteCriado?.();
        } catch (_e) { /* silencioso — convite é um plus, não bloqueia UX */ }
      } else {
        toast.error(detail);
      }
    } finally {
      setEnviando(false);
    }
  };

  const copyRegisterLink = async () => {
    const link = buildRegisterLink(emailNaoCadastrado);
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Link de cadastro copiado. Envie para o(a) convidado(a).');
    } catch (_e) {
      toast.error('Não foi possível copiar. Copie manualmente: ' + link);
    }
  };

  return (
    <div
      className="bg-white rounded-xl shadow-sm p-6 border border-orange-100"
      data-testid="transferir-responsavel-card"
    >
      <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-2">
        <UserCog className="w-5 h-5 text-orange-600" />
        Responsável pela organização
      </h2>
      <p className="text-gray-500 text-sm mb-4">
        A titularidade desta organização está vinculada a uma pessoa. Ela pode ser
        transferida para outro usuário do mesmo município que já tenha cadastro
        na plataforma. O antigo responsável perde o acesso à edição imediatamente
        após a transferência.
      </p>

      {/* Estado atual */}
      <div className="mb-5 flex items-center gap-3 bg-gray-50 rounded-lg p-3">
        <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
          <UserCog className="w-5 h-5 text-orange-600" />
        </div>
        <div className="min-w-0">
          <p className="text-xs text-gray-500">Responsável atual</p>
          <p className="text-sm font-medium text-gray-800 truncate" data-testid="responsavel-atual">
            {currentResponsavel?.nome || currentResponsavel?.email || currentResponsavel?.id || '—'}
          </p>
          {currentResponsavel?.email && currentResponsavel?.nome && (
            <p className="text-xs text-gray-500 truncate">{currentResponsavel.email}</p>
          )}
        </div>
      </div>

      {/* Estado: email não cadastrado → mostra link de cadastro para compartilhar */}
      {emailNaoCadastrado && (
        <div
          className="mb-4 p-4 bg-blue-50 rounded-xl border border-blue-200 space-y-3"
          data-testid="convite-nao-cadastrado-card"
        >
          <div className="flex items-start gap-2">
            <UserPlus className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
            <div className="text-sm text-blue-900">
              <p className="font-semibold">Essa pessoa ainda não tem cadastro</p>
              <p className="mt-1">
                O email <strong>{emailNaoCadastrado}</strong> não está registrado na
                plataforma. Compartilhe o link abaixo com o(a) convidado(a). Depois
                que ele(a) se cadastrar no mesmo município, volte aqui e refaça a
                transferência.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 bg-white rounded-lg border border-blue-200 px-3 py-2">
            <ExternalLink className="w-4 h-4 text-blue-500 shrink-0" />
            <code
              className="text-xs text-blue-800 flex-1 truncate"
              data-testid="link-cadastro-convidado"
            >
              {buildRegisterLink(emailNaoCadastrado)}
            </code>
            <button
              type="button"
              onClick={copyRegisterLink}
              className="p-1.5 hover:bg-blue-100 rounded transition-colors"
              title="Copiar link"
              data-testid="btn-copiar-link-cadastro"
            >
              <Copy className="w-4 h-4 text-blue-600" />
            </button>
          </div>
          <button
            type="button"
            onClick={() => setEmailNaoCadastrado(null)}
            className="text-sm text-blue-700 hover:underline"
            data-testid="btn-fechar-convite-info"
          >
            Ok, entendi
          </button>
        </div>
      )}

      {!confirmando ? (
        <div className="space-y-3">
          <label className="text-sm font-medium text-gray-700 flex items-center gap-1">
            <Mail className="w-4 h-4" /> Email do novo responsável
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="novo.responsavel@exemplo.com"
            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
            data-testid="input-novo-responsavel-email"
          />
          <button
            type="button"
            onClick={() => {
              if (!email.trim()) {
                toast.error('Informe o email do novo responsável.');
                return;
              }
              setConfirmando(true);
            }}
            className="w-full py-3 bg-orange-600 text-white rounded-xl font-medium hover:bg-orange-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="btn-transferir-responsavel"
          >
            <ArrowRightLeft className="w-4 h-4" />
            Transferir responsabilidade
          </button>
        </div>
      ) : (
        <div className="space-y-3 p-4 bg-orange-50 rounded-xl border border-orange-200">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-orange-600 shrink-0 mt-0.5" />
            <div className="text-sm text-orange-900">
              <p className="font-semibold">Confirmar transferência</p>
              <p className="mt-1">
                Após confirmar, <strong>{email}</strong> passará a ser o único responsável e
                você perderá o acesso à edição desta organização. Essa ação é imediata.
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setConfirmando(false)}
              disabled={enviando}
              className="flex-1 py-3 bg-white border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
              data-testid="btn-cancelar-transferencia"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={enviando}
              className="flex-1 py-3 bg-orange-600 text-white rounded-xl font-medium hover:bg-orange-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="btn-confirmar-transferencia"
            >
              {enviando ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Transferindo...
                </>
              ) : (
                'Sim, transferir'
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
